// Reconnect backoff for the SSE stream (notification.stream.ts): first
// retry after MIN, doubling per failure up to MAX. A successful connect
// resets to MIN — that also covers the server's routine ~10-minute
// stream rotation (it ends healthy streams so reconnects re-validate
// the auth token).
export const NOTIFICATION_STREAM_RECONNECT_MIN_MS = 1_000
export const NOTIFICATION_STREAM_RECONNECT_MAX_MS = 30_000

export const NOTIFICATION_FEED_LIMIT = 100

// Event discriminators, mirroring the writer side
// (v2-acc-data/staffNotification Code* constants).
export const NOTIFICATION_CODE_SELF_SERVICE_INVITE_CREATED =
  "SelfServiceInviteCreated"

// The threshold alerts api.Evaluate_Acc_StaffAlerts appends nightly (via
// v2-usage-sync). The usage rules mirror the HIGH arms of deriveChurnRisk
// (usage.scope.utils.ts) so an alert and the churn badge tell the same
// story; payloads carry the numbers as they stood at evaluation time.
export const NOTIFICATION_CODE_CUSTOMER_WENT_SILENT = "CustomerWentSilent"
export const NOTIFICATION_CODE_CUSTOMER_ACTIVITY_DROPPED =
  "CustomerActivityDropped"
export const NOTIFICATION_CODE_CUSTOMER_TRIAL_ENDING_SOON =
  "CustomerTrialEndingSoon"
export const NOTIFICATION_CODE_CUSTOMER_PAYMENT_OVERDUE =
  "CustomerPaymentOverdue"
// More seats in use than the live licenses allow (acc.CustomerSeatUsage);
// re-alerts every two weeks while it lasts. Seats are never enforced, so
// this and the over-cap face of an invite row are how sales hears of it.
export const NOTIFICATION_CODE_CUSTOMER_OVER_SEAT_CAP = "CustomerOverSeatCap"
// The customer's people kept meeting the license lock in the last 7 days —
// refused attempts on features their license does not carry, in the
// console or at an MCP server (acc.LicenseDeniedDaily, AIP-47). The one
// alert that is an opening, not a risk; re-alerts every two weeks while the
// knocking continues.
export const NOTIFICATION_CODE_CUSTOMER_LICENSE_DEMAND = "CustomerLicenseDemand"

// The notification center dock survives reloads/navigation the way the
// operator left it (NotificationCenterProvider).
export const NOTIFICATION_CENTER_OPEN_STORAGE_KEY = "notification-center-open"

export type NotificationCategoryId =
  | "all"
  | "churn"
  | "upsell"
  | "billing"
  | "trials"
  | "invites"

export type NotificationCategory = {
  id: NotificationCategoryId
  label: string
  /** Codes the tab shows; null = every code (the All tab). */
  codes: readonly string[] | null
  /** Empty-feed copy when the tab's filter matches nothing. */
  emptyLabel: string
}

// The dock's filter tabs, grouping event codes by the responsibility they
// hand the operator: save the account, sell the upgrade, collect the money,
// convert the trial, follow up the invite.
export const NOTIFICATION_CATEGORIES: readonly NotificationCategory[] = [
  {
    id: "all",
    label: "All",
    codes: null,
    emptyLabel: "Events like churn alerts and invitations will show up here.",
  },
  {
    id: "churn",
    label: "Churn risk",
    codes: [
      NOTIFICATION_CODE_CUSTOMER_WENT_SILENT,
      NOTIFICATION_CODE_CUSTOMER_ACTIVITY_DROPPED,
    ],
    emptyLabel: "No churn signals — nobody went silent or dropped sharply.",
  },
  {
    id: "upsell",
    label: "Upsell",
    codes: [NOTIFICATION_CODE_CUSTOMER_LICENSE_DEMAND],
    emptyLabel:
      "No upsell signals — nobody has been knocking on a license lock.",
  },
  {
    id: "billing",
    label: "Billing",
    codes: [
      NOTIFICATION_CODE_CUSTOMER_PAYMENT_OVERDUE,
      NOTIFICATION_CODE_CUSTOMER_OVER_SEAT_CAP,
    ],
    emptyLabel:
      "No billing alerts — nobody is past due or over their seat cap.",
  },
  {
    id: "trials",
    label: "Trials",
    codes: [NOTIFICATION_CODE_CUSTOMER_TRIAL_ENDING_SOON],
    emptyLabel: "No trials ending soon.",
  },
  {
    id: "invites",
    label: "Invites",
    codes: [NOTIFICATION_CODE_SELF_SERVICE_INVITE_CREATED],
    emptyLabel: "No self-service invitations yet.",
  },
]
